import { ChevronDown } from 'lucide-react';
import { AnimatePresence, motion } from 'motion/react';
import { useId, useRef, useState } from 'react';
import { RESULT_TYPES } from 'src/data/result-types.ts';
import { EASE_STANDARD, QUESTION_TRANSITION } from 'src/lib/motion.ts';
import { toneStyles } from 'src/lib/scoring.ts';
import { ResultType } from 'src/types/types.ts';

const POINTER: Record<ResultType['tone'], string> = {
  red: 'border-t-zeb-tone-red',
  darkorange: 'border-t-zeb-tone-darkorange',
  orange: 'border-t-zeb-tone-orange',
  lime: 'border-t-zeb-tone-lime',
  green: 'border-t-zeb-tone-green'
};

function ResultBadge({ className = '' }: { className?: string }) {
  return (
    <span
      className={`rounded-sm bg-zeb-primary px-2 py-0.5 text-micro font-semibold uppercase tracking-wider text-white ${className}`}>
      Ihr Ergebnis
    </span>
  );
}

/**
 * Übersicht der fünf Reifegrad-Stufen.
 *
 * Zwei getrennte Zustände:
 *  - `reached` — die aus dem Score erreichte Stufe; fix mit „Ihr Ergebnis"-Marker
 *                (zeb-primary), unabhängig von Klicks.
 *  - `shown`   — die aktuell angesehene Stufe (per Klick); Default = `reached`.
 *
 * Es ist immer genau eine Beschreibung sichtbar (die von `shown`).
 * Mobile: vertikales Akkordeon. Desktop: horizontale Skala + Detail-Panel.
 */
export function StageOverview({
  reached,
  className = ''
}: {
  reached: ResultType | null;
  className?: string;
}) {
  const baseId = useId();
  const [selectedName, setSelectedName] = useState<string | null>(null);
  const prevReachedNameRef = useRef(reached?.name);

  // Score-Wechsel → zurück zur erreichten Stufe.
  if (prevReachedNameRef.current !== reached?.name) {
    prevReachedNameRef.current = reached?.name;
    setSelectedName(null);
  }

  if (!reached) {
    return (
      <p className={`text-body-sm text-zeb-gray-500 ${className}`}>
        Für dieses Ergebnis liegen nicht genügend Angaben vor.
      </p>
    );
  }

  const shown = RESULT_TYPES.find((t) => t.name === selectedName) ?? reached;
  const handleSelect = (name: string) => setSelectedName((cur) => (cur === name ? null : name));

  return (
    <div className={className}>
      <ol className="space-y-3 md:hidden">
        {RESULT_TYPES.map((t) => {
          const tones = toneStyles[t.tone];
          const isReached = t.name === reached.name;
          const isShown = t.name === shown.name;
          const panelId = `${baseId}-m-${t.tone}`;
          const headerId = `${panelId}-h`;

          return (
            <li key={t.name}>
              <div
                className={`overflow-hidden rounded-md border bg-white transition-[box-shadow,border-color] ${
                  isReached
                    ? 'border-zeb-primary shadow-zeb-sm ring-2 ring-zeb-primary/20'
                    : 'border-zeb-muted shadow-zeb-xs'
                }`}>
                <button
                  id={headerId}
                  type="button"
                  onClick={() => handleSelect(t.name)}
                  aria-expanded={isShown}
                  aria-controls={panelId}
                  aria-current={isReached ? 'step' : undefined}
                  className="flex w-full items-center gap-4 px-5 py-4 text-left">
                  <span className={`h-2.5 w-2.5 shrink-0 rounded-full ${tones.dot}`} />
                  <span className="flex min-w-0 flex-1 flex-wrap items-baseline gap-x-3 gap-y-1">
                    <span
                      className="text-lg tracking-tight text-zeb-tertiary"
                      style={{
                        fontFamily: 'var(--font-heading)',
                        fontWeight: 700,
                        letterSpacing: '-0.02em'
                      }}>
                      {t.name}
                    </span>
                    {isReached && <ResultBadge />}
                  </span>
                  <span className="shrink-0 text-sm tabular-nums text-zeb-gray-500">
                    Score {t.range}
                  </span>
                  <ChevronDown
                    className={`h-4 w-4 shrink-0 text-zeb-gray-400 transition-transform duration-200 motion-reduce:transition-none ${
                      isShown ? 'rotate-180' : ''
                    }`}
                    aria-hidden="true"
                  />
                </button>

                <AnimatePresence initial={false}>
                  {isShown && (
                    <motion.div
                      key="panel"
                      id={panelId}
                      role="region"
                      aria-labelledby={headerId}
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: 'auto', opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      transition={{ duration: 0.25, ease: EASE_STANDARD }}
                      className="overflow-hidden">
                      <p
                        className="border-t border-zeb-muted px-5 py-4 text-body-sm leading-relaxed text-zeb-gray-700"
                        style={{ fontFamily: 'var(--font-body)', fontWeight: 500 }}>
                        {t.short}
                      </p>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            </li>
          );
        })}
      </ol>

      {/* DESKTOP — horizontale Skala + Detail-Panel */}
      <div className="hidden md:block">
        <ol className="grid grid-cols-5 gap-2">
          {RESULT_TYPES.map((t) => {
            const tones = toneStyles[t.tone];
            const isReached = t.name === reached.name;
            const isShown = t.name === shown.name;
            return (
              <li key={t.name}>
                <button
                  type="button"
                  onClick={() => handleSelect(t.name)}
                  aria-pressed={isShown}
                  aria-current={isReached ? 'step' : undefined}
                  className="group block w-full text-center">
                  <div className="flex h-6 items-end justify-center">
                    {isReached && <ResultBadge />}
                  </div>
                  <div className={`mx-1 mt-1.5 h-1.5 rounded-full ${tones.dot}`} />
                  <div className="flex h-2.5 items-start justify-center pt-1">
                    {isShown && (
                      <span
                        className={`block h-0 w-0 border-x-[7px] border-x-transparent border-t-[8px] ${POINTER[t.tone]}`}
                        aria-hidden="true"
                      />
                    )}
                  </div>
                  <div
                    className={`mt-1 rounded-md px-2 py-2 transition-colors ${
                      isShown ? 'bg-zeb-gray-100' : 'group-hover:bg-zeb-gray-100/60'
                    }`}>
                    <div
                      className="text-sm leading-snug tracking-tight text-zeb-tertiary"
                      style={{
                        fontFamily: 'var(--font-heading)',
                        fontWeight: 700,
                        letterSpacing: '-0.02em'
                      }}>
                      {t.name}
                    </div>
                    <div className="mt-1 text-xs tabular-nums text-zeb-gray-500">{t.range}</div>
                  </div>
                </button>
              </li>
            );
          })}
        </ol>

        <motion.div
          key={shown.name}
          initial={{ opacity: 0, y: 4 }}
          animate={{ opacity: 1, y: 0 }}
          transition={QUESTION_TRANSITION}
          aria-live="polite"
          className="mt-6 rounded-md border border-zeb-muted bg-white p-5 shadow-zeb-xs lg:p-6">
          <div className="flex items-baseline gap-3">
            <h3
              className="text-lg tracking-tight text-zeb-tertiary"
              style={{
                fontFamily: 'var(--font-heading)',
                fontWeight: 700,
                letterSpacing: '-0.02em'
              }}>
              {shown.name}
            </h3>
            <span className="text-sm tabular-nums text-zeb-gray-500">Score {shown.range}</span>
            {shown.name === reached.name && (
              <span className="ml-auto text-xs font-semibold text-zeb-primary">Ihr Ergebnis</span>
            )}
          </div>
          <p
            className="mt-2 max-w-3xl text-body-sm leading-relaxed text-zeb-gray-700"
            style={{ fontFamily: 'var(--font-body)', fontWeight: 500 }}>
            {shown.short}
          </p>
        </motion.div>
      </div>
    </div>
  );
}
